import { Button, Spacer, Stack } from '@pypestream/design-system';
import { FC, useMemo, useState } from 'react';

import { ChannelSettingsTemplateSections } from '../components/channel-settings-form/channel-settings-template-sections';
import {
  findAssignedRuleSetId,
  findBoundConnectionIds,
  toPlatformConnectionOptions,
  useChannelTemplateData,
} from '../components/channel-settings-form/channel-template-bindings';
import { useProStudioViewerRole } from '../hooks';
import { Channel } from '../utils';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import { waitForProStudioIdle } from '../xstate/wait-for-pro-studio-idle';

/**
 * A channel's Live Agent Platform, Reporting API and Auto-masking bindings in
 * the Design stage's channel panel — the prototype's `platformSections`
 * (`prostudio-build-flow-layouts.html`). The platforms, credentials and rule
 * sets themselves are project-level and managed in Project Settings; this only
 * shows what the channel is bound to and changes the binding, through the same
 * `assignPypeConnection` / `assignPypeRuleGroup` flows as Channel Settings.
 */
export const DesignChannelConnections: FC<{ channel: Channel }> = ({
  channel,
}) => {
  const isViewer = useProStudioViewerRole();
  const { pypeId, env } = channel;
  const [saving, setSaving] = useState(false);
  const {
    pypeRuleGroups,
    pypeConnections,
    credentials,
    ruleGroupsLoaded,
    ruleGroupsFailed,
    liveAgentLoaded,
    liveAgentFailed,
  } = useChannelTemplateData({ enabled: true, pypeId, env, isSaving: saving });

  const initialRuleSetId = useMemo(
    () => findAssignedRuleSetId(pypeRuleGroups, pypeId, env),
    [pypeRuleGroups, pypeId, env]
  );
  // Single-select, like Channel Settings: it shows (and switches) the first.
  const initialConnectionId = useMemo(
    () => findBoundConnectionIds(pypeConnections, pypeId, env)[0] ?? '',
    [pypeConnections, pypeId, env]
  );
  const platformConnections = useMemo(
    () => toPlatformConnectionOptions(pypeConnections, env),
    [pypeConnections, env]
  );

  // `null` = untouched (mirror the persisted value), as in Channel Settings.
  const [pickedRuleSetId, setPickedRuleSetId] = useState<string | null>(null);
  const [pickedConnectionId, setPickedConnectionId] = useState<string | null>(
    null
  );

  const ruleSetId = pickedRuleSetId ?? initialRuleSetId;
  const connectionId = pickedConnectionId ?? initialConnectionId;
  const maskingTouched = ruleSetId !== initialRuleSetId;
  const lapTouched = connectionId !== initialConnectionId;

  const reset = () => {
    setPickedRuleSetId(null);
    setPickedConnectionId(null);
  };

  const save = async () => {
    setSaving(true);
    const assignments: Array<() => void> = [];
    if (maskingTouched) {
      assignments.push(() =>
        sendProStudioEvent({
          type: 'assignPypeRuleGroup',
          payload: {
            pypeId,
            env,
            fromGroupId: initialRuleSetId || undefined,
            toGroupId: ruleSetId || undefined,
          },
        })
      );
    }
    if (lapTouched) {
      assignments.push(() =>
        sendProStudioEvent({
          type: 'assignPypeConnection',
          payload: {
            pypeId,
            env,
            fromConnectionId: initialConnectionId || undefined,
            toConnectionId: connectionId || undefined,
          },
        })
      );
    }

    // Both flows are handled only in the machine's `idle` state
    // (PSTUDIO-3897), so each waits for it. Waiting again after the last one
    // also covers a failed flow, which returns to `idle` via `botFlowError`
    // (and toasts its own error) without calling back.
    try {
      for (const dispatch of assignments) {
        // eslint-disable-next-line no-await-in-loop
        await waitForProStudioIdle(proStudioService);
        dispatch();
      }
      await waitForProStudioIdle(proStudioService);
    } catch {
      sendProStudioEvent({
        type: 'showError',
        error: 'Your changes could not be saved. Please try again.',
      });
    }
    // The flows write the new bindings back into context, so the "initial"
    // values now show what was actually saved.
    reset();
    setSaving(false);
  };

  return (
    <div
      className="c-design-channel-connections"
      data-testid="bobcat-design-channel-connections"
    >
      <ChannelSettingsTemplateSections
        ruleSets={pypeRuleGroups ?? []}
        ruleSetsLoaded={ruleGroupsLoaded}
        ruleSetsLoadFailed={ruleGroupsFailed}
        selectedRuleSetId={ruleSetId}
        onRuleSetChange={setPickedRuleSetId}
        platformConnections={platformConnections}
        platformConnectionsLoaded={liveAgentLoaded}
        platformConnectionsLoadFailed={liveAgentFailed}
        selectedConnectionId={connectionId}
        onSelectConnection={setPickedConnectionId}
        credentials={credentials}
        isViewer={isViewer}
        compact
      />
      {(maskingTouched || lapTouched) && (
        <>
          <Spacer size="small" />
          <Stack justifyContent="end" gutter="xsmall">
            <Button
              variant="secondary"
              size="small"
              disabled={saving}
              onClick={reset}
            >
              Cancel
            </Button>
            <Button
              size="small"
              test-id="bobcat-design-channel-connections-save"
              disabled={saving}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </>
      )}
    </div>
  );
};
